/*
  Parts explorer. Reads the JSON the parts-explorer section prints and wires:
  - drawing tabs (an index of the drawings, then one tab per Graco exploded view)
  - hotspots sitting exactly on Graco's printed callout numbers <-> rows in the parts list
  - zoom to the chosen part, search, wear-parts filter
  - add to cart per part (back ordered parts too), "Request" when the part has no product or price yet, or "Ask us" when a
    callout has no single confirmed part number
  - drawings that share one Graco parts list show only the rows whose callout they print (splitSharedLists)

  The drawings are Graco's own manual pages. Nothing here draws or places parts:
  every hotspot comes from seed/build_diagrams.py (the callout's position in the PDF),
  so a pointer can only land where Graco printed that number.
*/
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  // Symptoms painters describe, mapped to the part categories that usually fix them.
  const SYMPTOMS = [
    { id: 'prime', label: "Won't prime", cats: ['inlet_valve', 'prime_valve', 'suction', 'packings'], kits: true },
    { id: 'pressure', label: "Won't hold pressure", cats: ['outlet_valve', 'inlet_valve', 'packings', 'prime_valve', 'pressure_control'], kits: true },
    { id: 'tails', label: 'Tails or spitting', cats: ['tip', 'tip_guard', 'filter', 'pressure_control', 'gun'] },
    { id: 'leak', label: 'Leaking at the pump', cats: ['packings', 'pump'], kits: true },
    { id: 'gun', label: "Handpiece won't spray", cats: ['gun', 'tip', 'tip_guard', 'filter'] },
  ];

  // Painters' words for parts that Graco's manuals name differently. A search word also matches any word in its group.
  const SYNONYMS = [
    ['strainer', 'screen', 'filter', 'mesh', 'rock catcher', 'rock-catcher'],
    ['suction', 'inlet', 'intake', 'siphon', 'syphon'],
    ['packing', 'packings', 'seal', 'seals', 'o-ring', 'oring', 'v-packing'],
    ['hose', 'whip'],
    ['gun', 'handpiece', 'spray gun'],
    ['prime', 'priming', 'drain'],
  ];
  const variants = (word) => {
    const stem = word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word; // "strainers" finds "Strainer"
    const group = SYNONYMS.find((g) => g.includes(word) || g.includes(stem));
    return group ? group : [stem];
  };
  // Every word typed must appear in the part's text, directly or as one of its synonyms.
  const matches = (p, q) => {
    if (!q) return true;
    const hay = [p.title, p.part_number, p.ref, p.description, p.category, (p.aliases || []).join(' ')].join(' ').toLowerCase();
    return q.split(/\s+/).every((w) => variants(w).some((v) => hay.includes(v)));
  };

  // Sellable from the drawing only when the callout resolves to one part number for this machine.
  const sellable = (p) => !!(p.part_number && p.confirmed !== false);
  // Buyable when that part number is an active, priced product. Back ordered parts are still buyable (the variants sell
  // through on back order); the stock line under the name says when it ships. Only parts with no product or no price ask.
  const buyable = (p) => sellable(p) && !!p.variant_id && !!p.price;
  const addButton = (p, cls = '') => `<button type="button" class="btn btn--primary btn--sm part-add${cls}" data-part-add="${p.variant_id}" data-done-label="Added" aria-label="Add ${esc(p.title)} ${esc(p.part_number)} to cart">Add to cart</button>`;

  class PartsExplorer {
    constructor(root, data) {
      this.root = root;
      this.data = data;
      // Rows for other SKUs never reach the list (e.g. a 495-only part on a 490).
      const sku = this.data.sku;
      this.data.diagrams.forEach((d) => {
        // Join the drawing's purchasable products (keyed by variant SKU) onto its rows.
        const bySku = d.products || {};
        (d.parts || []).forEach((p) => { if (p.part_number && bySku[p.part_number]) Object.assign(p, bySku[p.part_number]); });
        delete d.products;
        d.parts = (d.parts || []).filter((p) => !sku || !p.skus || !p.skus.length || p.skus.includes(sku));
      });
      this.splitSharedLists();
      this.stage = $('[data-parts-stage]', root);
      this.canvas = $('[data-parts-canvas]', root);
      this.list = $('[data-parts-list]', root);
      this.detail = $('[data-part-detail]', root);
      this.search = $('[data-parts-search]', root);
      this.wearOnly = $('[data-parts-wear]', root);
      this.tabs = $('[data-parts-tabs]', root);
      this.caption = $('[data-parts-caption]', root);
      this.zoom = 1;
      this.current = null;
      this.activeRef = null;
      this.buildTabs();
      this.bind();
      const first = this.data.diagrams.find((d) => d.id === this.data.start) || this.data.diagrams[0];
      if (first) this.show(first.id);
    }

    // Graco prints one parts list for several views of the same assembly (the Ultra 390 Sprayer, Filter and Motor
    // drawings share one list), so each drawing carried every row. A row now shows on the drawings where its callout is
    // printed; a row printed on none of them stays on the first drawing that lists it, so no part goes missing.
    splitSharedLists() {
      const key = (d, p) => [d.manual, p.ref, p.part_number || '', p.title].join('|');
      const drawnOn = new Map();
      const firstOn = new Map();
      this.data.diagrams.forEach((d) => {
        const refs = new Set((d.hotspots || []).map((h) => String(h.ref)));
        d.parts.forEach((p) => {
          const k = key(d, p);
          if (!firstOn.has(k)) firstOn.set(k, d);
          if (refs.has(String(p.ref))) { if (!drawnOn.has(k)) drawnOn.set(k, new Set()); drawnOn.get(k).add(d); }
        });
      });
      this.data.diagrams.forEach((d) => {
        d.parts = d.parts.filter((p) => {
          const k = key(d, p);
          const on = drawnOn.get(k);
          return on ? on.has(d) : firstOn.get(k) === d;
        });
      });
    }

    buildTabs() {
      this.data.diagrams.unshift({ id: 'index', title: 'All drawings', parts: [] });
      this.tabs.innerHTML = this.data.diagrams.map((d) => `
        <button type="button" class="chip" role="tab" data-diagram="${esc(d.id)}" aria-selected="false">
          ${esc(d.title)}<span class="muted mono">${d.parts.length || ''}</span>
        </button>`).join('');
    }

    bind() {
      this.tabs.addEventListener('click', (e) => {
        const t = e.target.closest('[data-diagram]');
        if (t) this.show(t.dataset.diagram);
      });
      this.canvas.addEventListener('click', (e) => {
        const open = e.target.closest('[data-open-diagram]');
        if (open) { this.show(open.dataset.openDiagram); return; }
        const hs = e.target.closest('[data-hotspot]');
        if (hs) this.select(hs.dataset.hotspot, { from: 'diagram' });
        this.root.classList.add('is-touched');
      });
      this.list.addEventListener('click', (e) => {
        const add = e.target.closest('[data-part-add]');
        if (add) { e.stopPropagation(); this.add(add); return; }
        const row = e.target.closest('[data-part-row]');
        if (row) this.pick(row);
      });
      this.list.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const row = e.target.closest('[data-part-row]');
        if (row && e.target === row) { e.preventDefault(); this.pick(row); }
      });
      this.search.addEventListener('input', () => this.renderList());
      this.symptoms = $('[data-parts-symptoms]', this.root);
      if (this.symptoms) {
        this.symptoms.innerHTML = SYMPTOMS.map((x) => `<button type="button" class="chip" aria-pressed="false" data-symptom="${x.id}">${x.label}</button>`).join('');
        this.symptoms.addEventListener('click', (e) => {
          const b = e.target.closest('[data-symptom]');
          if (!b) return;
          this.symptom = this.symptom === b.dataset.symptom ? null : b.dataset.symptom;
          $$('[data-symptom]', this.symptoms).forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.symptom === this.symptom)));
          // jump to the drawing most likely to hold the fix
          const sym = SYMPTOMS.find((x) => x.id === this.symptom);
          if (sym) {
            const best = this.data.diagrams.find((dg) => dg.parts.some((p) => sym.cats.includes(p.category) && p.wear)) || this.data.diagrams.find((dg) => dg.parts.some((p) => sym.cats.includes(p.category)));
            if (best && best !== this.current) { const keep = this.symptom; this.show(best.id); this.symptom = keep; }
          }
          this.renderList();
        });
      }
      this.wearOnly.addEventListener('change', () => this.renderList());
      $$('[data-parts-zoom]', this.root).forEach((b) => b.addEventListener('click', () => {
        const dir = Number(b.dataset.partsZoom);
        this.setZoom(dir === 0 ? 1 : this.zoom + dir * 0.75);
      }));
      this.detail.addEventListener('click', (e) => {
        const add = e.target.closest('[data-part-add]');
        if (add) { this.add(add); return; }
        const ask = e.target.closest('[data-ask-part]');
        if (ask && window.ASE && ASE.chat) ASE.chat.open(ask.dataset.askPart);
      });
    }

    show(id) {
      const d = this.data.diagrams.find((x) => x.id === id);
      if (!d) return;
      this.current = d;
      this.activeRef = null;
      $$('[data-diagram]', this.tabs).forEach((t) => {
        const on = t.dataset.diagram === id;
        t.setAttribute('aria-selected', on);
        t.classList.toggle('is-active', on);
        if (on) t.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      });
      this.root.classList.toggle('is-index', id === 'index');
      this.root.classList.remove('is-searching');
      // A drawing's own note to the customer (Graco's two pump versions, a drawing that labels two parts the other way round).
      if (this.caption) { this.caption.textContent = d.caption || ''; this.caption.hidden = !d.caption; }
      if (id === 'index') {
        this.canvas.innerHTML = `<div class="parts__index">${this.data.diagrams.filter((x) => x.image).map((x) => `
          <button type="button" class="parts__thumb" data-open-diagram="${esc(x.id)}">
            <img src="${esc(x.thumb || x.image)}" alt="" loading="lazy" width="${x.width}" height="${x.height}">
            <span><strong>${esc(x.title)}</strong><span class="mono muted">${x.parts.length} parts</span></span>
          </button>`).join('')}</div>`;
        this.setZoom(1);
        this.renderList();
        this.closeDetail();
        return;
      }
      const art = `<img src="${esc(d.image)}" alt="${esc(d.title)} exploded view from Graco manual ${esc(d.manual || '')}" width="${d.width}" height="${d.height}" draggable="false">`;
      // One button over each printed callout number. A ref Graco prints twice gets two buttons.
      const spots = (d.hotspots || []).map((h) => {
        const p = d.parts.find((x) => String(x.ref) === String(h.ref));
        if (!p) return '';
        return `<button type="button" class="hotspot hotspot--callout${p.wear ? ' hotspot--wear' : ''}" data-hotspot="${esc(h.ref)}"
          style="left:${h.x}%;top:${h.y}%;width:${h.w || 2.4}%;height:${h.h || 2.4}%"
          aria-label="Ref ${esc(h.ref)}: ${esc(p.title)}${p.part_number ? `, ${esc(p.part_number)}` : ''}" title="Ref ${esc(h.ref)}: ${esc(p.title)}${p.part_number ? `, ${esc(p.part_number)}` : ''}"></button>`;
      }).join('');
      this.canvas.innerHTML = art + spots;
      this.setZoom(1);
      this.search.value = '';
      this.renderList();
      this.closeDetail();
    }

    renderList() {
      const d = this.current;
      if (!d) return;
      const q = this.search.value.trim().toLowerCase();
      const wear = this.wearOnly.checked;
      if (d.id === 'index') {
        // Searching from All drawings looks through every drawing; each result says which drawing it is on. The drawing
        // tiles step aside while a search is typed, so on a phone the results are not pushed below them.
        this.root.classList.toggle('is-searching', !!q);
        if (!q && !wear) {
          this.list.innerHTML = `<li class="part-row" style="cursor:default"><span></span><span class="muted">Pick a drawing, or search every drawing by part name or number. Each drawing is the exploded view from the Graco manual.</span><span></span></li>`;
          return;
        }
        const seen = new Set();
        const hits = [];
        this.data.diagrams.forEach((dg) => (dg.id === 'index' ? [] : dg.parts).forEach((p) => {
          if ((wear && !p.wear) || !matches(p, q)) return;
          const k = [p.part_number || '', p.ref, p.title].join('|');
          if (seen.has(k)) return;
          seen.add(k);
          hits.push({ p, dg });
        }));
        // The word as typed beats a synonym (strainer before filter), then buyable parts, then the usual priority.
        const direct = (p) => (String(p.title).toLowerCase().includes(q) ? 1 : 0);
        hits.sort((a, b) => direct(b.p) - direct(a.p) || (buyable(b.p) ? 1 : 0) - (buyable(a.p) ? 1 : 0) || (b.p.priority || 0) - (a.p.priority || 0));
        this.list.innerHTML = hits.length ? hits.map(({ p, dg }) => this.row(p, dg)).join('') : `<li class="muted" style="padding:16px">No parts match “${esc(q)}” on any drawing.</li>`;
        this.list.scrollTop = 0;
        return;
      }
      const sym = this.symptom ? SYMPTOMS.find((x) => x.id === this.symptom) : null;
      const rows = d.parts
        .filter((p) => (!wear || p.wear) && (!sym || sym.cats.includes(p.category) || (p.kit && sym.kits)) && matches(p, q))
        .sort((a, b) => (b.priority || 0) - (a.priority || 0) || (b.kit ? 1 : 0) - (a.kit ? 1 : 0) || (parseFloat(a.ref) || 0) - (parseFloat(b.ref) || 0));
      this.list.innerHTML = rows.length ? rows.map((p) => this.row(p)).join('') : `<li class="muted" style="padding:16px">No parts match “${esc(q)}”.</li>`;
      const act = this.activeRef != null && $(`[data-part-row="${CSS.escape(String(this.activeRef))}"]`, this.list);
      if (act) this.centreRow(act); else this.list.scrollTop = 0;
      $$('[data-hotspot]', this.canvas).forEach((h) => {
        h.classList.toggle('is-dim', !rows.some((p) => String(p.ref) === h.dataset.hotspot));
      });
    }

    // dg is set for a result found from All drawings: the row names that drawing and opens it when picked.
    row(p, dg) {
      const drawing = dg || this.current;
      let buy;
      if (buyable(p)) {
        buy = addButton(p);
      } else if (sellable(p)) {
        buy = `<a class="btn btn--ghost btn--sm" href="${esc(this.data.request_url)}?part=${encodeURIComponent(p.part_number)}&machine=${encodeURIComponent(this.data.machine)}">Request</a>`;
      } else {
        buy = `<a class="btn btn--ghost btn--sm" href="${esc(this.data.request_url)}?ref=${encodeURIComponent(p.ref)}&drawing=${encodeURIComponent(drawing.title)}&machine=${encodeURIComponent(this.data.machine)}">Ask us</a>`;
      }
      return `
        <li class="part-row${p.wear ? ' is-wear' : ''}${p.kit ? ' is-kit' : ''}${!dg && String(p.ref) === String(this.activeRef) ? ' is-active' : ''}" data-part-row="${esc(p.ref)}" data-pn="${esc(p.part_number || '')}"${dg ? ` data-goto="${esc(dg.id)}"` : ''} tabindex="0">
          <span class="part-row__ref${!p.kit && String(p.ref).length > 3 ? ' part-row__ref--word' : ''}">${p.kit ? 'KIT' : esc(p.ref)}</span>
          <span>
            <span class="part-row__name">${esc(p.title)}</span>
            <span class="part-row__meta">${dg ? `<span>${esc(dg.title)} drawing</span>` : ''}<span class="mono">${esc(p.part_number || 'Varies, ask us')}</span>${p.qty ? `<span>Qty ${esc(p.qty)}</span>` : ''}${p.variant_note ? `<span>${esc(p.variant_note)}</span>` : ''}${p.wear ? '<span>Wear part</span>' : ''}</span>${p.stock && buyable(p) ? `<span class="part-row__stock">${esc(p.stock)}</span>` : ''}
          </span>
          <span class="part-row__buy">${p.price && sellable(p) ? `<span class="price" style="font-size:14px">${esc(p.price)}</span>` : ''}${buy}</span>
        </li>`;
    }

    // A row from an All drawings search opens its drawing first, then points at the part as any row would.
    pick(row) {
      if (row.dataset.goto) this.show(row.dataset.goto);
      this.select(row.dataset.partRow, { from: 'list', pn: row.dataset.pn });
    }

    select(ref, { from, pn }) {
      const d = this.current;
      if (!d || d.id === 'index') return;
      const p = d.parts.find((x) => String(x.ref) === String(ref) && (!pn || x.part_number === pn)) || d.parts.find((x) => String(x.ref) === String(ref));
      this.activeRef = ref;
      $$('[data-hotspot]', this.canvas).forEach((h) => h.classList.toggle('is-active', h.dataset.hotspot === String(ref)));
      $$('[data-part-row]', this.list).forEach((r) => r.classList.toggle('is-active', r.dataset.partRow === String(ref)));
      const row = $(`[data-part-row="${CSS.escape(String(ref))}"]`, this.list);
      const h = (d.hotspots || []).find((x) => String(x.ref) === String(ref));
      if (h && from === 'list') this.focusPoint(h.x, h.y);
      if (p) this.openDetail(p);
      // Centre after the detail opens: it takes height from the flexing list, which would shift the centre.
      if (row) this.centreRow(row);
      this.revealBlock();
    }

    /* Centre a row inside the parts list by moving the list's own scrollTop, so the page never moves for it. */
    centreRow(row) {
      const list = this.list;
      // Trailing room so the last rows can sit mid-list too, instead of being stopped by the end of the scroll.
      list.style.paddingBottom = `${Math.round(list.clientHeight / 2)}px`;
      const top = row.offsetTop;
      const target = Math.max(0, Math.min(list.scrollHeight - list.clientHeight, top - (list.clientHeight - row.offsetHeight) / 2));
      list.scrollTo({ top: target, behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    /* The page only moves when it has to: wholly off screen, bring the block top into view; stacked (phones and
       tablets), or a sticky panel not yet pinned, nudge just far enough that the selected part's detail is on screen. */
    revealBlock() {
      const behavior = reduceMotion() ? 'auto' : 'smooth';
      const header = (document.querySelector('.site-header') || {}).offsetHeight || 64;
      const r = this.root.getBoundingClientRect();
      if (r.bottom < header || r.top > window.innerHeight) {
        window.scrollTo({ top: window.scrollY + r.top - header - 12, behavior });
        return;
      }
      if (this.detail.classList.contains('is-open')) {
        const dr = this.detail.getBoundingClientRect();
        const clear = window.matchMedia('(max-width: 989px)').matches ? 72 : 16; // phones: clearance for the chat label and sticky bar
        const over = dr.bottom - (window.innerHeight - clear);
        if (over > 0) window.scrollBy({ top: Math.min(over, Math.max(0, dr.top - header - 12)), behavior });
      }
    }

    openDetail(p) {
      const d = this.current;
      this.detail.innerHTML = `
        <div class="flex between items-center gap-8"><strong>${esc(p.title)}</strong><span class="mono muted">${esc(p.part_number || '')}</span></div>
        ${p.description ? `<p class="muted mb-0" style="font-size:14px">${esc(p.description)}</p>` : ''}
        ${buyable(p) ? `<p class="mb-0" style="font-size:14px"><span class="price">${esc(p.price)}</span>${p.stock ? `<span class="muted"> · ${esc(p.stock)}</span>` : ''}</p>` : ''}
        ${!sellable(p) ? `<p class="mb-0" style="font-size:14px">${esc(p.ask_reason || 'Graco lists more than one part number for this callout. Send us a photo of your serial plate and we’ll confirm the right one.')}</p>` : ''}
        <p class="mono muted mb-0" style="font-size:12px">Ref ${esc(p.ref)} · Graco manual ${esc(d.manual || '')}${d.page ? `, page ${esc(d.page)}` : ''}</p>
        <div class="flex gap-8 wrap">
          ${buyable(p) ? addButton(p) : ''}
          ${p.url && sellable(p) ? `<a class="btn btn--ghost btn--sm" href="${esc(p.url)}">Part details</a>` : ''}
          <button type="button" class="btn btn--ghost btn--sm" data-ask-part="Is ${esc(p.title)} (${esc(p.part_number || 'ref ' + p.ref)}) the right part for my ${esc(this.data.machine)}?">Not sure? Ask</button>
        </div>`;
      this.detail.classList.add('is-open');
    }
    closeDetail() { this.detail.classList.remove('is-open'); this.detail.innerHTML = ''; }

    setZoom(z) {
      this.zoom = Math.min(4, Math.max(1, z));
      if (this.zoom === 1) { this.canvas.style.transform = ''; return; }
      this.focusPoint(this.fx ?? 50, this.fy ?? 50, true);
    }
    // Manual drawings are dense, so picking a row zooms in on its callout.
    focusPoint(x, y, keep) {
      this.fx = x; this.fy = y;
      if (!keep && this.zoom < 2.25) this.zoom = 2.25;
      const z = this.zoom;
      const w = this.stage.clientWidth, h = this.stage.clientHeight;
      const tx = Math.min(0, Math.max(w - w * z, w / 2 - (x / 100) * w * z));
      const ty = Math.min(0, Math.max(h - h * z, h / 2 - (y / 100) * h * z));
      this.canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${z})`;
    }

    add(btn) {
      if (!window.ASE || !ASE.addWithFeedback) return;
      // Same feedback as every other add button: label flips to Added, cart count updates, drawer opens.
      ASE.addWithFeedback(btn, [{ id: Number(btn.dataset.partAdd), quantity: 1 }]);
    }
  }

  /* The drawings' data is not inline: it comes from <product url>?view=parts-data (templates/product.parts-data.liquid),
     fetched once when the explorer comes within about a screen of view, or straight away if a visitor reaches for it
     first (a tab, the search box, a #parts link). The section's own attributes carry the machine, SKU and request page. */
  const loaders = new WeakMap();
  function load(el) {
    if (loaders.has(el)) return loaders.get(el);
    const msg = $('[data-parts-loading]', el);
    // No Accept: application/json header: Shopify answers that with the product's own JSON instead of the view template.
    const p = fetch(el.dataset.partsSrc, { credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
      .then((t) => {
        const data = JSON.parse(t);
        if (!data || !Array.isArray(data.diagrams)) throw new Error('parts data');
        data.machine = el.dataset.machine || data.machine;
        data.sku = el.dataset.sku || data.sku;
        data.request_url = el.dataset.requestUrl || '/pages/contact';
        new PartsExplorer(el, data);
        if (msg) msg.remove();
        el.removeAttribute('aria-busy');
      })
      .catch(() => {
        loaders.delete(el);
        if (msg) msg.innerHTML = `The parts drawings didn't load. <button type="button" class="link" data-parts-retry>Try again</button>`;
      });
    loaders.set(el, p);
    return p;
  }

  document.querySelectorAll('[data-parts-explorer]').forEach((el) => {
    el.setAttribute('aria-busy', 'true');
    el.addEventListener('click', (e) => { if (e.target.closest('[data-parts-retry]')) load(el); });
    ['pointerdown', 'focusin'].forEach((ev) => el.addEventListener(ev, () => load(el), { once: true }));
    if (location.hash === '#parts') load(el);
    window.addEventListener('hashchange', () => { if (location.hash === '#parts') load(el); });
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((ens) => { if (ens.some((en) => en.isIntersecting)) { io.disconnect(); load(el); } }, { rootMargin: '800px 0px' });
      io.observe(el);
    } else {
      load(el);
    }
    // While the parts list is on screen, the product's sticky Add to cart bar steps aside so its button never sits
    // under a part's Add to cart; the CSS for it lives in the parts-explorer section.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => document.documentElement.classList.toggle('parts-in-view', en.isIntersecting), { rootMargin: '-80px 0px -80px 0px' }).observe(el);
    }
  });
})();
